import { Review, ReviewReply } from '../models/interfaces';
import { __TYPE__, REVIEW_STATUS } from '../models/enums';
import { DateTimeUtils } from './';

export class ReviewUtils {
  private static INTERNAL_REVIEW_STATUS = [
    REVIEW_STATUS.DONE,
    REVIEW_STATUS.PENDING,
  ];

  /**
   * Calculates a Bayesian adjusted rating.
   *
   * @param {number} averageRating - Current average rating (0-5).
   * @param {number} totalReviews - Total number of reviews.
   * @param {number} globalAverage - Average rating across all locations (default: 4.2).
   * @param {number} minimumReviews - Review count required for full confidence (default: 100).
   * @returns {number} Adjusted rating.
   */
  public static calculateAdjustedRating(
    averageRating: number,
    totalReviews: number,
    globalAverage = 4.2,
    minimumReviews = 100
  ) {
    if (!totalReviews || totalReviews < 0) {
      return 0;
    }

    return (
      (totalReviews / (totalReviews + minimumReviews)) * averageRating +
      (minimumReviews / (totalReviews + minimumReviews)) * globalAverage
    );
  }

  public static generateGoogleReviewUrl(placeId: string): string {
    return `https://search.google.com/local/writereview?placeid=${placeId}`;
  }

  public static isDoneReview(review: Review): boolean {
    return (
      review?.status === REVIEW_STATUS.DONE ||
      review?.status === REVIEW_STATUS.DONE_GMB
    );
  }

  public static isGmbPendingReview(review: Review): boolean {
    return (
      review?.status === REVIEW_STATUS.PENDING_GMB ||
      review?.status === REVIEW_STATUS.GMB
    );
  }

  public static transformReviewForBackend(review: Review): Review {
    return {
      ...review,
      __type__: review.__type__ || __TYPE__.BASIC_REVIEW,
    };
  }

  public static transformReviewForFrontend(review: Review): Review {
    if (!review) {
      return review;
    }

    return {
      ...review,
      lastModifiedAsDate: DateTimeUtils.parseStringToDate(review.lastModified),
      orderCheckoutDateAsDate: DateTimeUtils.parseStringToDate(
        review.orderCheckoutDate
      ),
      replies:
        review?.replies?.map((reply) => ({
          ...reply,
          timeAsDate: DateTimeUtils.parseStringToDate(reply.time),
        })) || [],
      isInternalReview:
        this.INTERNAL_REVIEW_STATUS.indexOf(review.status as REVIEW_STATUS) >=
        0,
    };
  }

  public static transformReviewReplyForFrontend(
    reviewReply: ReviewReply
  ): ReviewReply {
    if (!reviewReply) {
      return reviewReply;
    }
    return {
      ...reviewReply,
      timeAsDate: DateTimeUtils.parseStringToDate(reviewReply?.time),
    };
  }
}
